<template>
  <section class="shipping">
    <h2 class="section-title">发货中心</h2>
    <div class="tabs">
      <button
        type="button"
        :class="['tab', { active: activeTab === 'unshipped' }]"
        @click="activeTab = 'unshipped'"
      >
        未发货
      </button>
      <button
        type="button"
        :class="['tab', { active: activeTab === 'shipped' }]"
        @click="activeTab = 'shipped'"
      >
        已发货
      </button>
    </div>

    <ul v-show="activeTab === 'unshipped'" class="order-list">
      <li v-for="order in unshippedList" :key="order.id" class="order-card">
        <p class="line"><span class="k">时间：</span>{{ order.time }}</p>
        <p class="line"><span class="k">用户：</span>{{ order.userDisplay }}</p>
        <p class="line"><span class="k">数量：</span>{{ order.quantity }}</p>
        <div class="addr-line">
          <p class="addr-text">
            <span class="k">地址：</span>{{ order.address }}
          </p>
          <button type="button" class="link-copy" @click="copyAddress(order.address)">
            复制地址
          </button>
        </div>
        <div class="ship-row">
          <div class="track-block">
            <span class="track-label">运单号</span>
            <input
              v-model="order.trackingInput"
              type="text"
              class="track-input"
              inputmode="numeric"
            />
          </div>
          <button type="button" class="btn-ship" @click="ship(order)">
            发货
          </button>
        </div>
      </li>
    </ul>

    <ul v-show="activeTab === 'shipped'" class="order-list">
      <li v-for="order in shippedList" :key="order.id" class="order-card">
        <p class="line"><span class="k">时间：</span>{{ order.time }}</p>
        <p class="line"><span class="k">用户：</span>{{ order.userDisplay }}</p>
        <p class="line"><span class="k">数量：</span>{{ order.quantity }}</p>
        <div class="addr-line">
          <p class="addr-text">
            <span class="k">地址：</span>{{ order.address }}
          </p>
          <button type="button" class="link-copy" @click="copyAddress(order.address)">
            复制地址
          </button>
        </div>
        <div class="shipped-row">
          <div class="track-readonly">
            <span class="track-label">运单号</span>
            <span class="track-num">{{ order.trackingNumber }}</span>
          </div>
          <button type="button" class="btn-shipped" disabled>已发货</button>
        </div>
      </li>
    </ul>
  </section>
</template>

<script>
import { Toast } from "vant";
import { admin_order_list, ship_order } from "@/api";
import {
  ADMIN_LIST_STATUS_SHIPPED,
  ADMIN_LIST_STATUS_UNSHIPPED,
  mapAdminOrderRow,
} from "../helpers/adminOrders.js";

const FETCH_LIMIT = 50;

export default {
  name: "AdminShippingSection",
  data() {
    return {
      activeTab: "unshipped",
      unshippedList: [],
      shippedList: [],
      loadingUnshipped: false,
      loadingShipped: false,
      shippingOrderId: null,
    };
  },
  watch: {
    activeTab(tab) {
      this.ensureTabLoaded(tab);
    },
  },
  mounted() {
    this.ensureTabLoaded(this.activeTab);
  },
  methods: {
    ensureTabLoaded(tab) {
      const listKey = tab === "unshipped" ? "unshippedList" : "shippedList";
      const loadingKey =
        tab === "unshipped" ? "loadingUnshipped" : "loadingShipped";
      if (!this[listKey].length && !this[loadingKey]) {
        this.fetchTab(tab);
      }
    },
    /** 后台连续分页拉取，不在 UI 上增加「加载更多」 */
    async fetchAllRows(status) {
      const merged = [];
      let page = 1;
      for (;;) {
        const res = await admin_order_list({
          pages: String(page),
          limit: String(FETCH_LIMIT),
          status,
        });
        const rows = Array.isArray(res.data[0]) ? res.data[0] : [];
        merged.push(...rows);
        if (rows.length < FETCH_LIMIT) break;
        page += 1;
        if (page > 400) break;
      }
      return merged;
    },
    async fetchTab(tab) {
      const listKey = tab === "unshipped" ? "unshippedList" : "shippedList";
      const loadingKey =
        tab === "unshipped" ? "loadingUnshipped" : "loadingShipped";

      if (this[loadingKey]) return;

      const status =
        tab === "unshipped"
          ? ADMIN_LIST_STATUS_UNSHIPPED
          : ADMIN_LIST_STATUS_SHIPPED;

      this[loadingKey] = true;
      try {
        const rows = await this.fetchAllRows(status);
        this[listKey] = rows.map(mapAdminOrderRow);
      } catch (_) {
        this[listKey] = [];
      } finally {
        this[loadingKey] = false;
      }
    },
    async reloadBothTabs() {
      await Promise.all([
        this.fetchTab("unshipped"),
        this.fetchTab("shipped"),
      ]);
    },
    async copyAddress(text) {
      try {
        await navigator.clipboard.writeText(text);
        Toast.success("已复制");
      } catch (e) {
        Toast.fail("复制失败，请检查浏览器剪贴板权限或 HTTPS 环境");
      }
    },
    async ship(order) {
      if (this.shippingOrderId != null) return;
      const waybill = String(order.trackingInput || "").trim();
      if (!waybill) {
        Toast("请填写运单号");
        return;
      }
      this.shippingOrderId = order.id;
      try {
        await ship_order({
          id: String(order.id),
          waybill,
        });
        Toast.success("发货成功");
        this.unshippedList = [];
        this.shippedList = [];
        await this.reloadBothTabs();
      } catch (err) {
        const apiShape =
          err &&
          (typeof err.code === "number" ||
            typeof err.code === "string" ||
            typeof err.msg === "string");
        if (!apiShape) {
          Toast.fail(err?.message || "发货失败，请检查网络");
        }
      } finally {
        this.shippingOrderId = null;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.shipping {
  padding-bottom: 32px;
}

.section-title {
  margin: 0 0 20px;
  font-size: 32px;
  font-weight: 600;
  color: #1a1a1a;
}

.tabs {
  display: flex;
  gap: 40px;
  margin-bottom: 24px;
  border-bottom: 1px solid #eee;
}

.tab {
  position: relative;
  padding: 0 0 16px;
  border: none;
  background: none;
  font-size: 28px;
  color: #999;
  cursor: pointer;
}

.tab.active {
  color: #e6b141;
  font-weight: 600;
}

.tab.active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 4px;
  background: #e6b141;
  border-radius: 2px 2px 0 0;
}

.order-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.order-card {
  padding: 28px 0;
  border-bottom: 1px solid #f0f0f0;
}

.order-card:last-child {
  border-bottom: none;
}

.line {
  margin: 0 0 8px;
  font-size: 24px;
  line-height: 1.4;
  color: #333;
  word-break: break-all;
}

.k {
  color: #333;
  font-weight: 500;
}

.addr-line {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin: 8px 0 20px;
}

.addr-text {
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 24px;
  line-height: 1.45;
  color: #333;
  word-break: break-all;
}

.link-copy {
  flex-shrink: 0;
  border: none;
  background: none;
  padding: 0;
  font-size: 24px;
  color: #1890ff;
  cursor: pointer;
  text-decoration: underline;
}

.ship-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
}

.track-block {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.track-label {
  font-size: 24px;
  color: #333;
}

.track-input {
  width: 100%;
  height: 64px;
  padding: 0 16px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  background: #fff;
  font-size: 24px;
  color: #333;
  box-sizing: border-box;
}

.btn-ship {
  flex-shrink: 0;
  height: 64px;
  min-width: 120px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: #e5c486;
  color: #1a1a1a;
  font-size: 24px;
  font-weight: 500;
  cursor: pointer;
}

.shipped-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: 4px;
}

.track-readonly {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.track-num {
  font-size: 24px;
  color: #333;
  font-variant-numeric: tabular-nums;
}

.btn-shipped {
  flex-shrink: 0;
  height: 64px;
  min-width: 120px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  background: #dcdcdc;
  color: #666;
  font-size: 24px;
  cursor: not-allowed;
}
</style>
